import { useEffect, useState } from 'react';
import { useCanonical } from '@/hooks/useCanonical';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Clock, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { PLANITY_URL } from '@/data/services';

const inputClass =
  'w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-sm font-body focus:outline-none focus:ring-1 focus:ring-primary/40 transition placeholder:text-muted-foreground/50';

const labelClass = 'block text-xs font-heading font-medium text-foreground mb-1.5';

const Contact = () => {
  useCanonical('/contact');
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const [form, setForm] = useState({
    lastName: '',
    firstName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    document.title = 'Contact — Body by CC';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Contactez Body by CC, institut de beauté à Sevran. Appelez le 06 45 23 00 34 ou envoyez-nous un message.');
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    await new Promise((r) => setTimeout(r, 1000));
    toast({
      title: `Merci ${form.firstName} !`,
      description: 'Votre message a bien été envoyé. Nous reviendrons vers vous rapidement.',
      action: (
        <div className="shrink-0 w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
          <Check size={18} className="text-primary" strokeWidth={3} />
        </div>
      ),
    });
    navigate('/confirmation', { state: { form } });
  };

  return (
    <>
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-3xl md:text-5xl font-bold">
            Contactez-nous
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-5 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed font-body">
            Une question ? Envoyez-nous un message ou prenez directement rendez-vous en ligne.
          </motion.p>
        </div>
      </section>

      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">
          <motion.div className="lg:col-span-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h2 className="font-heading text-lg font-bold text-foreground mb-1">Formulaire de contact</h2>
            <p className="text-xs text-muted-foreground mb-8 font-body">Les champs marqués d'un * sont obligatoires</p>
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="lastName" className={labelClass}>Nom *</label>
                  <input type="text" id="lastName" name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} placeholder="Votre nom" required />
                </div>
                <div>
                  <label htmlFor="firstName" className={labelClass}>Prénom *</label>
                  <input type="text" id="firstName" name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} placeholder="Votre prénom" required />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>Email *</label>
                  <input type="email" id="email" name="email" value={form.email} onChange={handleChange} className={inputClass} placeholder="votre@email.fr" required />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>Téléphone</label>
                  <input type="tel" id="phone" name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="06 12 34 56 78" />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className={labelClass}>Sujet</label>
                <select id="subject" name="subject" value={form.subject} onChange={handleChange} className={inputClass}>
                  <option value="">Sélectionner</option>
                  <option value="rdv">Prise de rendez-vous</option>
                  <option value="info">Demande d'information</option>
                  <option value="soin">Question sur un soin</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className={labelClass}>Message *</label>
                <textarea id="message" name="message" value={form.message} onChange={handleChange} rows={6} className={`${inputClass} resize-none`} placeholder="Votre message..." required />
              </div>
              <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-heading font-medium text-sm hover:bg-primary/90 transition-colors disabled:opacity-60">
                {sending ? 'Envoi en cours…' : 'Envoyer'} <Send size={14} />
              </button>
            </form>
          </motion.div>

          <motion.div className="lg:col-span-2 space-y-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
            <div>
              <h3 className="font-heading font-semibold text-sm text-foreground mb-4">Coordonnées</h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-sm text-muted-foreground font-body">
                  <Phone size={16} className="text-primary shrink-0" />
                  <a href="tel:+33645230034" className="hover:text-primary transition-colors">06 45 23 00 34</a>
                </li>
                <li className="flex items-center gap-3 text-sm text-muted-foreground font-body">
                  <Mail size={16} className="text-primary shrink-0" />
                  <a href="mailto:kreent@yahoo.fr" className="hover:text-primary transition-colors">kreent@yahoo.fr</a>
                </li>
                <li className="flex items-start gap-3 text-sm text-muted-foreground font-body">
                  <MapPin size={16} className="text-primary shrink-0 mt-0.5" />
                  <span>65 Bd Westinghouse<br />93270 Sevran</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-muted-foreground font-body">
                  <Clock size={16} className="text-primary shrink-0 mt-0.5" />
                  <span>Lundi : Fermé<br />Mar-Dim : 10h-20h</span>
                </li>
              </ul>
            </div>
            <div className="p-5 rounded-xl border border-border bg-card">
              <h3 className="font-heading font-semibold text-sm text-foreground mb-2">Réservation en ligne</h3>
              <p className="text-xs text-muted-foreground leading-relaxed font-body mb-3">
                Pour prendre rendez-vous directement, utilisez notre plateforme Planity.
              </p>
              <a href={PLANITY_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-primary font-heading font-medium hover:underline">
                Réserver sur Planity →
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Contact;